import {
  type SideNavigationV2Props,
  type PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import { SpanCol } from "skedyul-ui/ui";
import { store } from "@store/index";
import {
  updateParams,
  close as closeSideNavigation,
} from "@store/features/layout/sideNavigationSlice";
import { refreshMyWorkspaceNav } from "@store/features/account/workplaceSlice";
import { toast } from "sonner";
import { trpc } from "@utils/api";
import {
  formatTeamWorkspaceImportIssueSummary,
  formatTeamWorkspaceImportPageDescription,
} from "./teamWorkspaceImportPreviewCopy";

type PagePreview = {
  path: string;
  title: string;
  modelHandle: string;
  action: "create" | "update";
  destructive: boolean;
  issues: Array<{
    code: string;
    modelHandle?: string;
    message?: string;
  }>;
};

type TeamWorkspaceImportPreviewParams = {
  schema?: unknown;
  fileName?: string;
  isLoading?: boolean;
  isApplying?: boolean;
  error?: string;
  preview?: {
    valid: boolean;
    error?: string;
    pages: PagePreview[];
    navigationItemCount: number;
    destructiveCount?: number;
  };
};

export const teamWorkspaceImportPreviewSchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }> & {
    props: TeamWorkspaceImportPreviewParams;
  },
): SideNavigationV2Props<PageV2Component> => {
  const { sideNavigation } = args.state;
  const isOpen = sideNavigation[args.position]?.isOpen ?? true;
  const params = (sideNavigation[args.position]?.params ??
    {}) as TeamWorkspaceImportPreviewParams;

  const isLoading = params.isLoading ?? false;
  const isApplying = params.isApplying ?? false;
  const error = params.error;
  const preview = params.preview;
  const pages = preview?.pages ?? [];
  const destructivePages = pages.filter((page) => page.destructive);
  const createPages = pages.filter((page) => !page.destructive);
  const issueSummary = formatTeamWorkspaceImportIssueSummary(pages);
  const hasDestructive = destructivePages.length > 0;

  const handleApply = async () => {
    if (!params.schema) return;
    store.dispatch(
      updateParams({
        position: args.position,
        params: { ...params, isApplying: true, error: undefined },
      }),
    );
    try {
      const result = await trpc.workplaces.applyTeamWorkspaceImport.mutate({
        schema: params.schema,
      });
      if (!result.success) {
        throw new Error(result.error || "Import failed");
      }
      toast.success(`Imported ${result.pagesApplied} team page(s)`);
      store.dispatch(closeSideNavigation({ position: args.position }));
      await store.dispatch(refreshMyWorkspaceNav());
    } catch (err) {
      store.dispatch(
        updateParams({
          position: args.position,
          params: {
            ...params,
            isApplying: false,
            error: err instanceof Error ? err.message : "Import failed",
          },
        }),
      );
    }
  };

  const loadingContent = isLoading
    ? [
        {
          type: "Card" as const,
          title: "Loading",
          props: {
            variant: "outline" as const,
            header: {
              title: "Analyzing workspace file…",
              description:
                "Please wait while we preview team pages and navigation.",
            },
          },
        },
      ]
    : [];

  const errorContent = error
    ? [
        {
          type: "Card" as const,
          title: "Error",
          props: {
            variant: "outline" as const,
            header: {
              title: "Error",
              description: error,
            },
          },
        },
      ]
    : [];

  const summaryContent =
    !isLoading && !error && preview?.valid
      ? [
          {
            type: "Card" as const,
            title: "Summary",
            props: {
              variant: "outline" as const,
              header: {
                title: params.fileName || "Workspace import",
                description: `${pages.length} team page(s)${
                  preview.navigationItemCount
                    ? ` · ${preview.navigationItemCount} nav section(s)`
                    : ""
                }${
                  hasDestructive
                    ? ` · ${destructivePages.length} will overwrite existing pages`
                    : ""
                }${issueSummary ? ` · ${issueSummary}` : ""}`,
              },
            },
          },
        ]
      : [];

  const destructiveContent =
    !isLoading && !error && preview?.valid && hasDestructive
      ? [
          {
            type: "Card" as const,
            title: "Destructive Changes",
            props: {
              variant: "outline" as const,
              header: {
                title: `Destructive Changes (${destructivePages.length})`,
                description:
                  "These pages already exist and will be fully replaced, including layout, blocks, and model binding.",
              },
              form: {
                formVersion: "v1" as const,
                fields: destructivePages.map((page, index) => ({
                  id: `destructive-${index}`,
                  component: "Alert" as const,
                  span: 12 as SpanCol,
                  mdSpan: 12 as SpanCol,
                  lgSpan: 12 as SpanCol,
                  props: {
                    icon: "Trash",
                    title: `Overwrite: ${page.title}`,
                    description: formatTeamWorkspaceImportPageDescription(page),
                    variant: "destructive" as const,
                  },
                })),
              },
            },
          },
        ]
      : [];

  const createContent =
    !isLoading && !error && preview?.valid && createPages.length > 0
      ? [
          {
            type: "Card" as const,
            title: "New Pages",
            props: {
              variant: "outline" as const,
              header: {
                title: `New Pages (${createPages.length})`,
                description: "These pages will be created.",
              },
              form: {
                formVersion: "v1" as const,
                fields: createPages.map((page, index) => ({
                  id: `create-${index}`,
                  component: "Alert" as const,
                  span: 12 as SpanCol,
                  mdSpan: 12 as SpanCol,
                  lgSpan: 12 as SpanCol,
                  props: {
                    icon: "Plus",
                    title: `Create: ${page.title}`,
                    description: formatTeamWorkspaceImportPageDescription(page),
                    variant: "default" as const,
                  },
                })),
              },
            },
          },
        ]
      : [];

  const actionsContent =
    !isLoading && !error && preview?.valid
      ? [
          {
            type: "Card" as const,
            title: "Actions",
            props: {
              variant: "outline" as const,
              form: {
                formVersion: "v1" as const,
                fields: [
                  {
                    id: "cancel-btn",
                    component: "Button" as const,
                    span: 6 as SpanCol,
                    mdSpan: 6 as SpanCol,
                    lgSpan: 6 as SpanCol,
                    props: {
                      label: "Cancel",
                      variant: "outline" as const,
                      className: "w-full",
                      isDisabled: isApplying,
                      handleClick: () => {
                        store.dispatch(
                          closeSideNavigation({ position: args.position }),
                        );
                      },
                    },
                  },
                  {
                    id: "apply-btn",
                    component: "Button" as const,
                    span: 6 as SpanCol,
                    mdSpan: 6 as SpanCol,
                    lgSpan: 6 as SpanCol,
                    props: {
                      label: isApplying
                        ? "Applying…"
                        : hasDestructive
                          ? "Apply (Destructive)"
                          : "Apply import",
                      variant: hasDestructive
                        ? ("destructive" as const)
                        : ("default" as const),
                      className: "w-full",
                      isLoading: isApplying,
                      isDisabled: pages.length === 0,
                      handleClick: () => {
                        void handleApply();
                      },
                    },
                  },
                ],
              },
            },
          },
        ]
      : [];

  const closeOnlyContent =
    !isLoading && error
      ? [
          {
            type: "Card" as const,
            title: "Actions",
            props: {
              variant: "outline" as const,
              form: {
                formVersion: "v1" as const,
                fields: [
                  {
                    id: "close-btn",
                    component: "Button" as const,
                    span: 12 as SpanCol,
                    mdSpan: 12 as SpanCol,
                    lgSpan: 12 as SpanCol,
                    props: {
                      label: "Close",
                      variant: "outline" as const,
                      className: "w-full",
                      handleClick: () => {
                        store.dispatch(
                          closeSideNavigation({ position: args.position }),
                        );
                      },
                    },
                  },
                ],
              },
            },
          },
        ]
      : [];

  return {
    side: args.position,
    isOpen,
    width: args.width || "600px",
    component: {
      component: "PageV2",
      props: {
        title: "Import team workspace",
        hideTitle: false,
        header: {
          icon: "Upload",
        },
        content: {
          structure: "sidebar",
          components: [
            ...loadingContent,
            ...errorContent,
            ...summaryContent,
            ...destructiveContent,
            ...createContent,
            ...actionsContent,
            ...closeOnlyContent,
          ],
        },
      },
    },
  };
};
